<template>
  <div class="settlement-sheet sheet">
    <header class="header">
      <button
        type="button"
        class="text-vp-primary text-lg font-black"
        @click.prevent="close"
      >
        X
      </button>
    </header>
    <main class="main">
      <template v-if="settlementSheet">
        <BoxedFrame title="Based on ticket sales from">
          <section>
            <Form
              :form-id="settlementTypeId"
              :initial-values="settlementSheet"
              action="#"
            >
              <div class="section">
                <VPDropdown
                  input-id="settlementType"
                  placeholder="Choose settlement type"
                  :options="settlementTypeOptions"
                />
              </div>
              <div v-if="settlementType === 'TICKET_SALES_EVENTS'">
                <div class="section section-row div-5 px-2">
                  <div class="row-label triple-f">
                    Date
                  </div>
                  <div class="row-label right-field">
                    Doors
                  </div>
                  <div class="row-label right-field">
                    Show
                  </div>
                </div>
                <div
                  v-for="event in availableEvents"
                  :key="event.id"
                  class="section section-row div-5 event"
                  :class="{ selected: isEventSelected(event) }"
                  @click="toggleEventSelected(event)"
                >
                  <div class="triple-f">
                    {{ formatDate(event.date) }}
                  </div>
                  <div class="right-field">
                    {{ formatTime(event.doorTime) }}
                  </div>
                  <div class="right-field">
                    {{ formatTime(event.startTime) }}
                  </div>
                </div>
                <template v-if="isEventSelected">
                  <VPCheckbox
                    label="Select all dates"
                    :value="allDatesSelected"
                    :true-value="true"
                    :false-value="false"
                    @change="setAllDatesSelected"
                  />
                </template>
              </div>
              <div
                v-if="settlementType === 'MANUAL_GBOR'"
                class="with-border"
              >
                <div class="section">
                  Manual ticket sales
                </div>
                <div class="section section-row div-5 section-header">
                  <div class="double-f">
                    Description
                  </div>
                  <div class="right-field">
                    Tickets sold
                  </div>
                  <div class="right-field">
                    Price
                  </div>
                  <div class="right-field">
                    Total
                  </div>
                </div>
                <template
                  v-for="(row, index) in manualTicketsRows"
                  :key="row._formId"
                >
                  <ManualTicketSalesRow
                    :form-id="row._formId"
                    :initial-values="(manualTickets && manualTickets[index]) || {}"
                    :total="row.total"
                    :disabled="loading"
                    class="section section-row div-5 with-border"
                    @blur="onManualTicketRowBlur(row._formId)"
                    @save="onManualTicketSave(row._formId)"
                    @clear="onManualTicketDelete(row._formId)"
                  />
                </template>
              </div>
            </Form>
          </section>
        </BoxedFrame>
        <BoxedFrame title="Ticket deal">
          <section>
            <template
              v-for="(input, i) in ticketDeals"
              :key="i"
            >
              <SettlementDeal
                :deal-form-id="ticketDeals[i].dealType"
                :initial-values="(ticketDeals && ticketDeals[i]) || {}"
                type-of-deal="ticket"
                class="with-border"
              />
            </template>
          </section>
        </BoxedFrame>
        <BoxedFrame title="Bar/rental deals">
          <section>
            <template
              v-for="(input, i) in barDeals"
              :key="i"
            >
              <SettlementDeal
                :deal-form-id="barDeals[i].dealType"
                :initial-values="(barDeals && barDeals[i]) || {}"
                type-of-deal="bar"
                class="with-border"
              />
            </template>
          </section>
        </BoxedFrame>
        <BoxedFrame title="Adjustment">
          <section>
            <div class="section section-header section-row div-5 pb-1">
              <div>Amount</div>
              <div>Due</div>
              <div>Deducted type</div>
              <div class="double-f">
                Description
              </div>
            </div>
            <template
              v-for="(key, index) in customExpenseKeys"
              :key="key"
            >
              <AdjustExpense
                :form-id="key"
                :initial-values="(customExpenses && customExpenses[index]) || {}"
                :disabled="loading"
                @blur="onCustomExpenseBlur(key)"
                @clear="onCustomExpenseDelete(key)"
              />
            </template>
          </section>
        </BoxedFrame>
        <BoxedFrame title="Payments">
          <section />
        </BoxedFrame>
        <BoxedFrame
          title="Tax details"
          class="settlement-taxes"
        >
          <section>
            <TaxDetails />
          </section>
        </BoxedFrame>
      </template>
      <div
        v-else
        class="text-vp-base font-bold text-5xl content-center absolute top-24 left-24"
      >
        Loading...
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Form, useFormsStore } from 'vuniform';
import BoxedFrame from '@/components/BoxedFrame.vue';
import VPDropdown from '@/components/form/VPDropdown.vue';
import VPCheckbox from '@/components/form/VPCheckbox.vue';
import { useSettlementSheetStore } from '@/stores/settlement-sheet';
import { useSettlementSheetFormStore } from '@/stores/settlement-sheet-form';
import { SettlementTypeEnum, UpdateEventArtistSettlementInput } from '../../generated/graphql';
import { formatDate } from '@/helpers/dateTime';
import { formatTime } from '@/helpers/dateTime';
import ManualTicketSalesRow from './ManualTicketSalesRow.vue';
import SettlementDeal from './SettlementDeal.vue';
import AdjustExpense from './AdjustExpense.vue';
import TaxDetails from './TaxDetails.vue';

const settlementSheetStore = useSettlementSheetStore();
const settlementSheet = computed(() => settlementSheetStore.settlementSheet);
const settlementSheetFormStore = useSettlementSheetFormStore();
const formsStore = useFormsStore();
const route = useRoute();
const router = useRouter();
const loading = computed(() => settlementSheetStore.settlementSheetLoading);

const manualTickets = computed(() => settlementSheetStore.settlementSheet?.manualTicketSales);
const manualTicketsRows = computed(() => settlementSheetFormStore.manualTicketsRows);
const setAllDatesSelected = (selected: boolean) => settlementSheetFormStore.setAllDatesSelected(selected);

const ticketDeals = computed(() => settlementSheetStore.settlementSheet?.ticketDeals);
const barDeals = computed(() => settlementSheetStore.settlementSheet?.barDeals);

const customExpenses = computed(() => settlementSheetStore.settlementSheet?.customExpenses);
const customExpenseKeys = computed(() => settlementSheetFormStore.customExpenseFormIds);

const initSettlementSheet = async () => {
  await settlementSheetStore.fetchSettlementSheet(Number(route.params.settlementId));
  settlementSheetFormStore.initFromSettlementSheet();
};

onMounted(initSettlementSheet);

const close = () => router.push('/');

const settlementTypeOptions = [
  {
    name: 'Selected shows',
    value: SettlementTypeEnum.TicketSalesEvents,
  },
  {
    name: 'Manual GBOR',
    value: SettlementTypeEnum.ManualGbor,
  },
];

const isEventSelected = settlementSheetFormStore.isEventSelected;
const toggleEventSelected = event => {
  settlementSheetFormStore.toggleEventSelected(event.id);
  settlementSheetStore.updateSelectedEvents(settlementSheetFormStore.selectedEventIds);
};
const settlementTypeId = 'settlement-sheet-type';

const settlementType = computed(() => formsStore.fieldGetValue(settlementTypeId, 'settlementType'));
const availableEvents = computed(() => settlementSheetStore.settlementSheet.availableEvents);
const allDatesSelected = settlementSheetFormStore.allDatesSelected;

const onManualTicketRowBlur = (key: string) => {
  onManualTicketSave(key);
};

const deleteManualTicketRow = (formId: string) => {
  settlementSheetFormStore.deleteManualTicketRow(formId);
};

const onManualTicketDelete = async (formId: string) => {
  const id = formsStore.fieldGetValue(formId, 'id');
  if (!id) {
    return;
  }

  // const payload = {
  //   manualTicketSales: {
  //     [formId]: {
  //       _destroy: true,
  //       id,
  //     },
  //   },
  // };

  await settlementSheetStore.saveManualTicket({ id, _destroy: true });

  deleteManualTicketRow(formId);
};

const onManualTicketSave = async (formId: string) => {
  if (loading.value || !formsStore.isFormDirty(formId)) {
    return;
  }

  const payload = formsStore.formGetValues<UpdateEventArtistSettlementInput>(formId);
  payload.clientMutationId = formId;

  await settlementSheetStore.saveManualTicket(payload);

  formsStore.SET_INITIAL_VALUES({
    values: { ...payload },
    formId,
    clearDirty: true,
  });

  formsStore.RESET_FIELD_VALUES(formId);
};

// const deleteCustomExpenseRow = (formId: string) => {
//   settlementSheetFormStore.deleteManualTicketRow(formId);
// };

const onCustomExpenseBlur = (key: string) => {
  onCustomExpenseSave(key);
};

const onCustomExpenseDelete = async (formId: string) => {
  const id = formsStore.fieldGetValue(formId, 'id');
  if (!id) {
    return;
  }

  // const payload = {
  //   manualTicketSales: {
  //     [formId]: {
  //       _destroy: true,
  //       id,
  //     },
  //   },
  // };

  await settlementSheetStore.saveCustomExpense({ id, _destroy: true });
};

const onCustomExpenseSave = async (formId: string) => {
  if (loading.value || !formsStore.isFormDirty(formId)) {
    return;
  }

  const payload = formsStore.formGetValues<UpdateEventArtistSettlementInput>(formId);

  await settlementSheetStore.saveCustomExpense(payload);

  formsStore.SET_INITIAL_VALUES({
    values: { ...payload },
    formId,
    clearDirty: true,
  });

  formsStore.RESET_FIELD_VALUES(formId);
};
</script>
